/**
 * Logic behind the preview blocks of the "ABC" dialogs: preview text, picture
 * transform and the arrow-key nudging of a picture under the mouse.
 */
import { useEffect, useRef, type CSSProperties, type RefObject } from 'react';

export const PREVIEW_TEXT = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ';
export const ZOOM_STEP = 0.05;
export const INITIAL_ZOOM = 0.35;

export const previewText = (maxChars: unknown): string =>
  PREVIEW_TEXT.slice(0, Number(maxChars) || 13);

/** `transform` of a picture from its scale and offsets. */
export const imageTransform = (props: {
  scale?: number | null | undefined;
  offsetX?: number | null | undefined;
  offsetY?: number | null | undefined;
}): CSSProperties => ({
  transform: `translate(${props.offsetX ?? 0}px, ${props.offsetY ?? 0}px) scale(${(props.scale ?? 100) / 100})`,
});

const ARROWS: Record<string, [number, number]> = {
  ArrowLeft: [-1, 0],
  ArrowRight: [1, 0],
  ArrowUp: [0, -1],
  ArrowDown: [0, 1],
};

/**
 * Arrow keys move the picture whose row is under the mouse, while the
 * dialog itself or a file input has the focus.
 */
export const useArrowNudge = <K extends string>(
  rows: Record<K, RefObject<HTMLElement | null>>,
  onNudge: (key: K, dx: number, dy: number) => void,
) => {
  const mouseOver = useRef<EventTarget | null>(null);
  const handler = useRef(onNudge);
  useEffect(() => {
    handler.current = onNudge;
  });

  useEffect(() => {
    const onMouseOver = (event: Event) => {
      mouseOver.current = event.target;
    };
    const onKeyDown = (event: KeyboardEvent) => {
      const active = document.activeElement;
      if (
        !active ||
        !(
          active.tagName === 'DIALOG' ||
          active.classList.contains('field-file-input')
        )
      )
        return;
      const move = ARROWS[event.key];
      if (!move) return;
      const target = mouseOver.current;
      if (!(target instanceof Node)) return;
      const key = (Object.keys(rows) as K[]).find((entry) =>
        rows[entry].current?.contains(target),
      );
      if (!key) return;
      handler.current(key, move[0], move[1]);
      event.preventDefault();
    };
    document.addEventListener('mouseover', onMouseOver);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('mouseover', onMouseOver);
      document.removeEventListener('keydown', onKeyDown);
    };
    // The refs are stable for the life of the dialog.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
};
